import type { StatusChipVariant } from '@/components/ui/status-chip';
import type { ChargeStatus, FeeRun, FeeRunStatus, FeeRunUserStatus, FundsStatus, GuardName, GuardResult } from '@/services/api/fee-runs';
import { formatEuros } from '@/utils/format';

/** Statuses in which the run is still moving and the page should keep polling. */
const ACTIVE_STATUSES: ReadonlySet<FeeRunStatus> = new Set(['CALCULATING', 'COLLECTING', 'FINALISING']);

export const isActiveFeeRunStatus = (status: FeeRunStatus | undefined): boolean => status !== undefined && ACTIVE_STATUSES.has(status);

export const FEE_RUN_STATUS_LABEL: Record<FeeRunStatus, string> = {
  CALCULATING: 'Calculating',
  CALCULATED: 'Ready to review',
  CALCULATION_FAILED: 'Calculation failed',
  COLLECTING: 'Collecting',
  FINALISING: 'Finalising',
  COMPLETED: 'Completed',
  PARTIAL: 'Completed with failures',
  CANCELLED: 'Cancelled',
};

export const feeRunStatusVariant = (status: FeeRunStatus): StatusChipVariant => {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'CALCULATED':
    case 'COLLECTING':
    case 'FINALISING':
    case 'CALCULATING':
      return 'info';
    case 'PARTIAL':
      return 'warning';
    case 'CALCULATION_FAILED':
      return 'destructive';
    default:
      return 'muted';
  }
};

export const userStatusVariant = (status: FeeRunUserStatus): StatusChipVariant => {
  switch (status) {
    case 'DONE':
      return 'success';
    case 'IN_PROGRESS':
      return 'info';
    case 'PARTIAL':
      return 'warning';
    case 'FAILED':
      return 'destructive';
    default:
      return 'muted';
  }
};

export const chargeStatusVariant = (status: ChargeStatus): StatusChipVariant => {
  switch (status) {
    case 'CHARGED':
      return 'success';
    case 'SKIPPED':
    case 'UNKNOWN':
      return 'warning';
    case 'FAILED':
      return 'destructive';
    default:
      return 'muted';
  }
};

/** Shared chip sizing for the per-user and per-charge status chips in tables and dialogs. */
export const CHIP_CLASS = 'px-2 py-0.5 text-xs';

/** One sentence for the operator on what an UNKNOWN charge means and what happens to it. */
export const UNKNOWN_CHARGE_EXPLANATION =
  'The exchange did not answer, so this debit may or may not have applied; the next Retry checks the exchange’s adjustments log and settles it either way.';

export const fundsStatusVariant = (status: FundsStatus): StatusChipVariant => {
  switch (status) {
    case 'RECORDED':
      return 'success';
    case 'FAILED':
      return 'destructive';
    case 'PENDING':
      return 'info';
    default:
      return 'muted';
  }
};

export interface FeeRunProgress {
  /** Account holders that have settled (done, partial or failed) */
  settled: number;
  total: number;
  /** 0–100, whole numbers */
  percent: number;
}

/** Collection progress from the run's atomic counters against its planned user count. */
export const feeRunProgress = (run: Pick<FeeRun, 'counters' | 'totals'>): FeeRunProgress => {
  const total = run.totals?.users ?? 0;
  const settled = run.counters.usersDone + run.counters.usersPartial + run.counters.usersFailed;
  const percent = total === 0 ? 0 : Math.min(100, Math.round((settled / total) * 100));
  return { settled, total, percent };
};

/** The guards that block approval, in the order the API evaluated them. */
export const failedGuards = (guards: GuardResult[] | undefined): GuardResult[] => (guards ?? []).filter((guard) => !guard.passed);

/** What each safety check means: `title` is the check as listed, `clause` is how it reads in an "approval blocked" sentence. */
export const GUARD_LABEL: Record<GuardName, { title: string; clause: string }> = {
  PRICE_COMPLETENESS: { title: 'Every coin has a EUR price', clause: 'a coin has no EUR price' },
  SNAPSHOT_COMPLETENESS: { title: 'Every balance source has a fresh snapshot', clause: 'a balance source has no snapshot in the lookback window' },
  PER_USER_CEILING: { title: 'No account holder over the per-user ceiling', clause: 'an account holder would pay over the ceiling' },
  RUN_CEILING: { title: 'Total fees under the run ceiling', clause: 'the total is over the run ceiling' },
  STALENESS: { title: 'Calculation is recent enough to approve', clause: 'the calculation is too old — calculate again' },
  SINGLE_ACTIVE_RUN: { title: 'No other run is collecting', clause: 'another run is still collecting' },
};

/** The API sends money as decimal strings; anything that is not a finite number is shown as sent. */
export const formatEurString = (value: string | undefined | null): string => {
  if (value === undefined || value === null || value === '') return '—';
  const number = Number(value);
  return Number.isFinite(number) ? formatEuros(number) : value;
};

const CHARGE_DATE_FORMAT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

/** `5 October 2026`, from the run's YYYY-MM-DD charge date. */
export const formatChargeDate = (run: Pick<FeeRun, 'chargeDate'>): string => {
  const date = new Date(`${run.chargeDate}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? run.chargeDate : CHARGE_DATE_FORMAT.format(date);
};

/** Which run-level actions the page offers for a status; the API enforces the same rules. */
export const feeRunActions = (status: FeeRunStatus): { canApprove: boolean; canCancel: boolean; canRetry: boolean } => ({
  canApprove: status === 'CALCULATED',
  canCancel: status === 'CALCULATING' || status === 'CALCULATED' || status === 'CALCULATION_FAILED',
  canRetry: status === 'COLLECTING' || status === 'FINALISING' || status === 'PARTIAL',
});
